import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getBaseDomainFromHost, makeTenantUrl, getRootUrl } from '../utils/tenantUrl';
import { LICENSES } from '../utils/legalText';
import SupportModal from '../components/SupportModal';
import LoginHelpBot from '../components/LoginHelpBot';
import { Building2, X, ChevronRight, Globe } from 'lucide-react';
import './Login.css';
import { normalizeWorkspaceInput } from '../utils/workspaceNormalize';
import {
  consumeSessionExpiredFlash,
  SESSION_EXPIRED_MESSAGE,
  INVALID_LOGIN_MESSAGE,
  getFriendlyErrorMessage,
  isTechnicalErrorText
} from '../utils/errorMessages';
import ieFlagUrl from '../assets/flags/ie.svg';
import gbFlagUrl from '../assets/flags/gb.svg';

const WORKSPACE_REGEX = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** Display name for workspace picker subtitle (visual only). */
function getCountryDisplayName(code) {
  if (!code) return '';
  const u = String(code).toUpperCase();
  const map = {
    IE: 'Ireland',
    GB: 'United Kingdom',
    UK: 'United Kingdom',
    US: 'United States',
    FR: 'France',
    DE: 'Germany',
    NL: 'Netherlands',
    ES: 'Spain',
    IT: 'Italy',
    PT: 'Portugal',
    PL: 'Poland',
    BE: 'Belgium',
    AT: 'Austria',
    CH: 'Switzerland',
    SE: 'Sweden',
    NO: 'Norway',
    DK: 'Denmark',
    FI: 'Finland',
    AU: 'Australia',
    NZ: 'New Zealand',
    CA: 'Canada'
  };
  return map[u] || u;
}

function workspaceCardVariant(countryCode) {
  const u = countryCode ? String(countryCode).toUpperCase() : '';
  if (u === 'IE') return 'ie';
  if (u === 'GB' || u === 'UK') return 'gb';
  return 'default';
}

/** SVG flags bundled via webpack so paths work with any PUBLIC_URL. */
function workspaceCardFlagSrc(variant) {
  if (variant === 'ie') return ieFlagUrl;
  if (variant === 'gb') return gbFlagUrl;
  return null;
}

const TERMS_OF_SERVICE = `1ï¸âƒ£ Terms of Service (Hosted SaaS)

Terms of Service
Last updated: February 2026

These Terms of Service ("Terms") govern access to and use of the SimplyInventory service ("Service"), operated by Meyer Software Services.
By accessing or using the Service, you agree to be bound by these Terms.

________________________________________

1. The Service
SimplyInventory is a hosted, cloud-based software service provided over the internet.
Customers access the Service via a web interface and/or API.
No software is sold, licensed, or delivered to the customer.

________________________________________

2. Access & Use
â€¢ You are granted a non-exclusive, non-transferable right to access the Service during your subscription term.
â€¢ You may use the Service only for lawful business purposes.
â€¢ You may not attempt to reverse engineer, copy, or resell the Service.

________________________________________

3. Customer Data
â€¢ You retain ownership of all data you submit to the Service.
â€¢ We process data solely to provide and maintain the Service.
â€¢ We implement reasonable security measures to protect customer data.

________________________________________

4. Availability
â€¢ We aim to provide high availability but do not guarantee uninterrupted access.
â€¢ Maintenance, updates, or outages may occur.

________________________________________

5. Fees & Payment
â€¢ Fees are agreed separately in a commercial agreement or invoice.
â€¢ Failure to pay may result in suspension or termination of access.

________________________________________

6. Intellectual Property
â€¢ All rights, title, and interest in the Service remain with Meyer Software Services.
â€¢ These Terms do not grant ownership or licensing rights to the underlying software.

________________________________________

7. Termination
We may suspend or terminate access if these Terms are violated or if required by law.

________________________________________

8. Limitation of Liability
To the maximum extent permitted by law, we are not liable for indirect, incidental, or consequential damages.

________________________________________

9. Governing Law
These Terms are governed by the laws of England and Wales.

________________________________________

10. Contact
For questions about these Terms, contact: izak.meyer@simplyinventory.co.uk

________________________________________

Hosted Service Only
The Service is provided exclusively as a hosted, cloud-based solution.
No executable software, source code, or binaries are delivered, licensed, or transferred to the customer under these Terms.`;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [workspace, setWorkspace] = useState('');
  const [workspaceError, setWorkspaceError] = useState('');
  const [lookupEmail, setLookupEmail] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [lookupResult, setLookupResult] = useState([]);
  const [lookupError, setLookupError] = useState('');
  const [workspaceLookupMode, setWorkspaceLookupMode] = useState('workspace'); // 'workspace' | 'email'
  const [workspaceResolveLoading, setWorkspaceResolveLoading] = useState(false);
  const [countryPickerTenants, setCountryPickerTenants] = useState(null); // [{ subdomain, name, countryCode }] when multiple
  const [showTerms, setShowTerms] = useState(false);
  const [showLicenses, setShowLicenses] = useState(false);
  const [showSupportModal, setShowSupportModal] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const isRootOrWww = typeof window !== 'undefined' && (() => {
    const h = window.location.hostname;
    const base = getBaseDomainFromHost(h);
    return h === base || h.toLowerCase() === 'www.' + base;
  })();
  const showWorkspaceBox = isRootOrWww;

  const handleWorkspaceGo = async (e) => {
    e.preventDefault();
    setWorkspaceError('');
    const raw = workspace || '';
    const normalized = normalizeWorkspaceInput(raw);
    if (!normalized) {
      setWorkspaceError('Enter your workspace (for example your company name).');
      return;
    }
    const baseDomain = getBaseDomainFromHost(typeof window !== 'undefined' ? window.location.hostname : '');
    const w = normalized.toLowerCase();
    let queryWorkspace = normalized;

    if (w.includes('://')) {
      try {
        const parsed = new URL(raw.trim());
        const hostname = (parsed.hostname || '').toLowerCase();
        if (hostname === baseDomain || hostname === 'www.' + baseDomain) {
          setWorkspaceError('Could not find a workspace in that web address.');
          return;
        }
        if (hostname.endsWith('.' + baseDomain)) {
          const label = hostname.slice(0, hostname.length - baseDomain.length - 1);
          if (!WORKSPACE_REGEX.test(label)) {
            setWorkspaceError('That web address does not look like a valid workspace link.');
            return;
          }
          queryWorkspace = label;
        } else {
          setWorkspaceError('Could not find a workspace in that web address.');
          return;
        }
      } catch (_) {
        setWorkspaceError('Invalid URL');
        return;
      }
    } else if (w.includes('.')) {
      let parsedTenantHost = false;
      try {
        const parsed = new URL('https://' + raw.trim());
        const hostname = (parsed.hostname || '').toLowerCase();
        if (hostname.endsWith('.' + baseDomain) && hostname !== baseDomain && hostname !== 'www.' + baseDomain) {
          const label = hostname.slice(0, hostname.length - baseDomain.length - 1);
          if (WORKSPACE_REGEX.test(label)) {
            queryWorkspace = label;
            parsedTenantHost = true;
          }
        }
      } catch (_) {
        /* not a valid URL host â€” treat as a company-style name (e.g. Acme Ltd.) */
      }
      if (!parsedTenantHost) {
        queryWorkspace = normalized;
      }
    }

    setWorkspaceResolveLoading(true);
    setCountryPickerTenants(null);
    const apiBase = process.env.REACT_APP_API_URL || '';
    try {
      const res = await fetch(`${apiBase}/api/public/workspace-resolve?workspace=${encodeURIComponent(queryWorkspace)}`);
      const data = await res.json();
      if (data.single && data.tenant) {
        const url = makeTenantUrl(data.tenant.subdomain);
        if (url) window.location.assign(url);
        return;
      }
      if (!data.single && data.tenants && data.tenants.length > 1) {
        setCountryPickerTenants(data.tenants);
        return;
      }
      setWorkspaceError(data.error || 'No workspace found.');
    } catch (err) {
      setWorkspaceError('Could not look up your workspace. Please try again.');
    } finally {
      setWorkspaceResolveLoading(false);
    }
  };

  const apiBase = process.env.REACT_APP_API_URL || '';
  const handleFindWorkspace = async (e) => {
    e.preventDefault();
    setLookupError('');
    setLookupResult([]);
    const em = (lookupEmail || '').trim();
    if (!em) {
      setLookupError('Enter your email');
      return;
    }
    setLookupLoading(true);
    try {
      const res = await fetch(`${apiBase}/api/public/workspace-lookup?email=${encodeURIComponent(em)}`);
      const data = await res.json();
      if (!res.ok) {
        setLookupError(data.error || 'Lookup failed');
        return;
      }
      const list = Array.isArray(data.workspaces) ? data.workspaces : [];
      setLookupResult(list);
      if (list.length === 0) setLookupError('No workspace found for this email.');
    } catch (err) {
      setLookupError('Could not look up workspace. Try again.');
    } finally {
      setLookupLoading(false);
    }
  };

  useEffect(() => {
    if (consumeSessionExpiredFlash()) {
      setError(SESSION_EXPIRED_MESSAGE);
    }
  }, []);

  useEffect(() => {
    if (user) {
      // Must change password first (admin/manager first login)
      if (user.mustChangePassword) {
        navigate('/change-password', { replace: true });
        return;
      }
      // Redirect based on role
      const roleRoutes = {
        power_user: '/power-user',
        admin: '/admin',
        manager: '/manager',
        site: '/site'
      };
      navigate(roleRoutes[user.role] || '/login', { replace: true });
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      const result = await login(email, password);
      if (!result.success) {
        const err = result.error || '';
        const lower = err.toLowerCase();
        if (lower.includes('base_domain')) {
          setError(err);
        } else if (lower.includes('tenant subdomain')) {
          setError('Please go to your workspace URL to log in. Enter your workspace above and click Find Workspace.');
        } else if (
          !err ||
          isTechnicalErrorText(err) ||
          /invalid credentials/i.test(err) ||
          /password not configured/i.test(err)
        ) {
          setError(INVALID_LOGIN_MESSAGE);
        } else {
          setError(
            getFriendlyErrorMessage(
              { response: { status: 401, data: { error: err, code: 'INVALID_LOGIN' } } },
              { kind: 'login' }
            ) || INVALID_LOGIN_MESSAGE
          );
        }
      } else if (result.mustChangePassword) {
        // Redirect to change password page
        navigate('/change-password', { replace: true });
      } else if (result.user?.role) {
        // Explicit redirect so we don't rely on useEffect (avoids race with fetchUser)
        const roleRoutes = {
          power_user: '/power-user',
          admin: '/admin',
          manager: '/manager',
          site: '/site'
        };
        navigate(roleRoutes[result.user.role] || '/login', { replace: true });
      }
    } catch (err) {
      console.error('Login submission error:', err);
      setError('An unexpected error occurred. Please try again.');
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-logo-container">
          <img 
            src={`${process.env.PUBLIC_URL || ''}/logo.png`} 
            alt="SimplyInventory Logo" 
            className="login-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <h1>SimplyInventory</h1>
        <h2>{showWorkspaceBox ? 'Find Workspace' : 'Sign In'}</h2>
        {showWorkspaceBox && (
          <div className="workspace-box" style={{ marginBottom: '20px', padding: '16px', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }} role="group" aria-label="How to find workspace">
              <button
                type="button"
                className={`btn btn-secondary ${workspaceLookupMode === 'workspace' ? 'btn-primary' : ''}`}
                style={{ flex: 1, fontSize: '14px' }}
                onClick={() => { setWorkspaceLookupMode('workspace'); setLookupError(''); setLookupResult([]); }}
              >
                By workspace
              </button>
              <button
                type="button"
                className={`btn btn-secondary ${workspaceLookupMode === 'email' ? 'btn-primary' : ''}`}
                style={{ flex: 1, fontSize: '14px' }}
                onClick={() => { setWorkspaceLookupMode('email'); setWorkspaceError(''); }}
              >
                By work email
              </button>
            </div>
            {workspaceLookupMode === 'workspace' && (
              <>
                <form onSubmit={handleWorkspaceGo}>
                  <div className="form-group">
                    <label>Workspace</label>
                    <input placeholder="e.g. Company Name" value={workspace} onChange={(e) => setWorkspace(e.target.value)} aria-label="Workspace" />
                  </div>
                  {workspaceError && <div className="error">{workspaceError}</div>}
                  <button type="submit" className="btn btn-primary" disabled={workspaceResolveLoading}>{workspaceResolveLoading ? 'Findingâ€¦' : 'Find Workspace'}</button>
                </form>
              </>
            )}
            {workspaceLookupMode === 'email' && (
              <>
                <form onSubmit={handleFindWorkspace}>
                  <div className="form-group">
                    <label>Work email</label>
                    <input type="email" placeholder="e.g. user@email.com" value={lookupEmail} onChange={(e) => setLookupEmail(e.target.value)} aria-label="Email for workspace lookup" />
                  </div>
                  {lookupError && <div className="error">{lookupError}</div>}
                  <button type="submit" className="btn btn-primary" disabled={lookupLoading}>{lookupLoading ? 'Looking upâ€¦' : 'Find Workspace'}</button>
                </form>
                {lookupResult.length > 0 && (
                  <div style={{ marginTop: '12px' }}>
                    <p style={{ margin: '0 0 6px', fontWeight: 600 }}>We found your workspace{lookupResult.length > 1 ? 's' : ''}:</p>
                    {lookupResult.map((ws) => {
                      const url = makeTenantUrl(ws.subdomain);
                      return url ? (
                        <p key={ws.id || ws.subdomain} style={{ margin: '4px 0' }}>
                          â†’ <a href={url} onClick={(e) => { e.preventDefault(); window.location.assign(url); }} style={{ color: '#0d6efd' }}>{url}</a>
                        </p>
                      ) : null;
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        )}
        {showWorkspaceBox && (
          <p style={{ marginTop: '16px' }}>
            <Link to="/admin-login" className="login-power-user-link">Power User login</Link>
          </p>
        )}
        {!showWorkspaceBox && (
          <>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Email</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
              </div>
              <div className="form-group">
                <label>Password</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
              </div>
              {error && <div className="error">{error}</div>}
              <button type="submit" className="btn btn-primary">Sign In</button>
            </form>
            <p className="login-tagline">Inventory made simpler</p>
            <p style={{ marginTop: '12px', fontSize: '14px' }}><a href={getRootUrl()} style={{ color: 'var(--hana-grey)' }}>Back to find workspace</a></p>
          </>
        )}
      </div>
      <footer className="login-footer">
        <button type="button" className="login-terms-link" onClick={() => setShowTerms(true)}>Terms of Service</button>
        <span className="login-footer-sep"> | </span>
        <button type="button" className="login-terms-link" onClick={() => setShowLicenses(true)}>Licenses</button>
        <span className="login-footer-sep"> | </span>
        <button type="button" className="login-terms-link" onClick={() => setShowSupportModal(true)}>Support</button>
      </footer>
      {countryPickerTenants && countryPickerTenants.length > 0 && (
        <div className="terms-overlay workspace-picker-overlay" onClick={() => setCountryPickerTenants(null)}>
          <div className="workspace-picker-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="workspace-picker-title">
            <div className="workspace-picker-header">
              <div className="workspace-picker-header-icon-wrap" aria-hidden="true">
                <Building2 className="workspace-picker-header-icon" strokeWidth={1.75} />
              </div>
              <div className="workspace-picker-header-text">
                <h2 id="workspace-picker-title" className="workspace-picker-title">Select your workspace</h2>
                <p className="workspace-picker-subtitle">Choose the one that matches your organisation</p>
              </div>
              <button type="button" className="workspace-picker-close" onClick={() => setCountryPickerTenants(null)} aria-label="Close">
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="workspace-picker-divider" />
            <div className="workspace-picker-body">
              {countryPickerTenants.map((t) => {
                const url = makeTenantUrl(t.subdomain);
                const cc = t.countryCode ? String(t.countryCode).toUpperCase() : '';
                const variant = workspaceCardVariant(t.countryCode);
                const countryName = getCountryDisplayName(t.countryCode);
                const badge = cc ? cc.slice(0, 2) : 'â€”';
                const metaLine = countryName && cc ? `${countryName} - ${cc}` : (countryName || cc || '');
                const flagSrc = workspaceCardFlagSrc(variant);
                return url ? (
                  <button
                    key={t.subdomain}
                    type="button"
                    className={`workspace-picker-card workspace-picker-card--${variant}`}
                    onClick={() => window.location.assign(url)}
                  >
                    <span className="workspace-picker-flag-wrap" aria-hidden="true">
                      {flagSrc ? (
                        <img src={flagSrc} alt="" className="workspace-picker-flag-img" />
                      ) : (
                        <span className="workspace-picker-flag-fallback">{badge}</span>
                      )}
                    </span>
                    <div className="workspace-picker-card-text">
                      <span className="workspace-picker-org-name">{t.name || t.subdomain}</span>
                      {metaLine ? (
                        <span className="workspace-picker-org-meta">
                          <Globe size={14} strokeWidth={2} className="workspace-picker-globe" aria-hidden="true" />
                          {metaLine}
                        </span>
                      ) : null}
                    </div>
                    <ChevronRight className="workspace-picker-chevron" size={22} strokeWidth={2} aria-hidden="true" />
                  </button>
                ) : null;
              })}
            </div>
            <div className="workspace-picker-footer">
              <button type="button" className="workspace-picker-btn-cancel" onClick={() => setCountryPickerTenants(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
      {showTerms && (
        <div className="terms-overlay" onClick={() => setShowTerms(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-header">
              <h3>Terms of Service</h3>
              <button type="button" className="terms-close" onClick={() => setShowTerms(false)} aria-label="Close">Ã—</button>
            </div>
            <div className="terms-content"><pre>{TERMS_OF_SERVICE}</pre></div>
            <div className="terms-footer"><button type="button" className="btn btn-secondary" onClick={() => setShowTerms(false)}>Close</button></div>
          </div>
        </div>
      )}
      {showLicenses && (
        <div className="terms-overlay" onClick={() => setShowLicenses(false)}>
          <div className="terms-modal" onClick={(e) => e.stopPropagation()}>
            <div className="terms-header">
              <h3>Licenses</h3>
              <button type="button" className="terms-close" onClick={() => setShowLicenses(false)} aria-label="Close">Ã—</button>
            </div>
            <div className="terms-content">
              <pre>{LICENSES}</pre>
            </div>
            <div className="terms-footer">
              <button type="button" className="btn btn-secondary" onClick={() => setShowLicenses(false)}>Close</button>
            </div>
          </div>
        </div>
      )}
      <SupportModal
        open={showSupportModal}
        onClose={() => setShowSupportModal(false)}
        tenantName={!showWorkspaceBox && typeof window !== 'undefined' ? window.location.hostname : ''}
      />
      {showWorkspaceBox && <LoginHelpBot />}
    </div>
  );
};

export default Login;



